import isWithinInterval from 'date-fns/isWithinInterval';
import parseISO from 'date-fns/parseISO';
import {DayName} from '@lib/infrastructure';
import {getBuildingId, getChildren, getNode} from '../../util/buildingNodes/treeHelpers';
import {IsNodeClosedArgs, IsParentClosedArgs} from './types';
import {format} from 'date-fns';

export const isDayClosed = (day: DayName, openingDays: DayName[]) => {
  return openingDays.length ? !openingDays.includes(day) : false;
};

const isParentClosed = ({node, exceptions, buildings, floors, areas}: IsParentClosedArgs): boolean => {
  if (!node || !node.parentId) {
    return false;
  }

  const parentNode = getNode(node.parentId, buildings, floors, areas);
  if (!parentNode) {
    return false;
  }

  const exceptionIncludingNode = exceptions.find((exception) => {
    return exception.closedNodeIds.includes(parentNode.id);
  });
  if (exceptionIncludingNode) {
    return true;
  }

  return isParentClosed({
    node: parentNode,
    exceptions,
    buildings,
    floors,
    areas,
  });
};

export const isNodeClosed = ({
  nodeId,
  date,
  buildings,
  floors,
  areas,
  openingDaysExceptions,
}: IsNodeClosedArgs): boolean => {
  if (!nodeId || !date) {
    return false;
  }

  const formattedDate = format(date, 'EEEE') as DayName;

  const node = getNode(nodeId, buildings, floors, areas);
  if (!node) {
    return false;
  }

  const buildingId = getBuildingId(node);
  const openingDays = buildings[buildingId]?.openingDays || [];

  if (isDayClosed(formattedDate, openingDays)) {
    return true;
  }

  if (openingDaysExceptions && openingDaysExceptions.length) {
    const exceptionsOnDate = openingDaysExceptions.filter(({startDate, endDate}) =>
      isWithinInterval(date, {
        start: parseISO(startDate),
        end: parseISO(endDate),
      }),
    );

    if (exceptionsOnDate && exceptionsOnDate.length) {
      const exceptionIncludingNode = exceptionsOnDate.find((exception) => {
        return exception.closedNodeIds.includes(nodeId);
      });
      // Is directly closed
      if (exceptionIncludingNode) {
        return true;
      }

      // Is parent closed
      if (isParentClosed({node, exceptions: exceptionsOnDate, buildings, floors, areas})) {
        return true;
      }
    }
  }

  const children = getChildren(node);
  if (children.length) {
    // All children closed
    return children.every((childNodeId) =>
      isNodeClosed({
        nodeId: childNodeId,
        date,
        buildings,
        floors,
        areas,
        openingDaysExceptions,
      }),
    );
  }

  return false;
};
